import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { FileData, FilePayload } from '@/libs/fleet-api/services/types';
import { Col, Form, Row, Space, Alert, Typography, Progress, Flex } from 'antd';
import { FC, useState, useEffect, useCallback } from 'react';
import { useModals } from '@/components/modals/ModalProvider';
import { useApp } from '@/App';
import {
	MPPRequestSLIKResponse,
	useRequestMPPSlikMutation,
	useSubmitMPPSlikRequestFilesMutation,
	useMPPSLIKResult,
} from '@/libs/fleet-api/services/mpp';
import ManualSLIKInputModal from './ManualSLIKInputModal';
import { SlikRobotikResult } from '@/libs/fleet-api/services/prospect';

export const useRequestSLIKModal = () =>
	useAsyncModal<
		{
			customerId: string;
			slikFormFile?: FileData;
			applicationLetterFile?: FileData;
			groupName: 'FRM' | 'CCA' | 'CCDH';
			spfId?: number;
			mppSlikInquiryId?: number; // Add inquiry ID if available
			isReviewing?: boolean;
		},
		MPPRequestSLIKResponse
	>();

export type RequestSLIKModalProps = ReturnType<typeof useRequestSLIKModal>;

const RequestSLIKModal: FC<RequestSLIKModalProps> = ({
	modal,
	close,
	data,
}) => {
	const isReviewing = data?.isReviewing ?? false;
	const { openPreviewModal } = useModals();
	const { navigate } = useApp();
	const [requestStep, setRequestStep] = useState<
		'initial' | 'loading' | 'requesting' | 'reference' | 'manual' | 'result'
	>('initial');
	const [slikInquiryId, setSlikInquiryId] = useState<number | null>(null);
	const [referenceNumber, setReferenceNumber] = useState<string>('');
	const [showManualModal, setShowManualModal] = useState(isReviewing);

	// Simulation mode - change to test different SLIK scenarios
	// Options: "none", "manual", "robotik_completed"
	const SIMULATION_MODE: string = 'none';

	const requestMPPSlikMutation = useRequestMPPSlikMutation();
	const submitSlikRequestFilesMutation = useSubmitMPPSlikRequestFilesMutation();

	// For CCA: Check SLIK result - use inquiry ID from props if available, otherwise use state
	const slikResultQuery = useMPPSLIKResult(
		(data?.mppSlikInquiryId && data.mppSlikInquiryId > 0) ||
			(slikInquiryId && slikInquiryId && slikInquiryId > 0)
			? data?.mppSlikInquiryId || slikInquiryId!
			: undefined,
	);

	const isFRM = data?.groupName === 'FRM';
	const isCCA = data?.groupName === 'CCA';

	const handleNavigateToHistory = useCallback(() => {
		navigate('HistorySLIKMPPPage', {
			mppSlikInquiryId: data?.mppSlikInquiryId || 0,
			isReviewing: true,
			onBack: () => {
				navigate('MPPListPage');
			},
		});
		close();
	}, [navigate, data, close]);

	// Check for existing SLIK result when modal opens for CCA
	useEffect(() => {
		if (isCCA && slikResultQuery.data) {
			const [result] = slikResultQuery.data;

			// Debug logging
			console.log('📊 Current API Response:', {
				isManualSlik: result.isManualSlik,
				isEnableSlikRobotic: result.isEnableSlikRobotic,
				roboticSlikResult: result.roboticSlikResult,
				roboticSlikRequestResult: result.roboticSlikRequestResult,
				simulationMode: SIMULATION_MODE,
			});

			// Set inquiry ID from the result if available
			if (data?.mppSlikInquiryId) {
				setSlikInquiryId(data.mppSlikInquiryId);
			}

			// SIMULATION: Force different SLIK states for testing
			if (SIMULATION_MODE === 'manual') {
				console.log('SIMULATION MODE: Forcing manual SLIK state');
				setShowManualModal(true);
				return;
			}

			if (SIMULATION_MODE === 'robotik_completed') {
				console.log('SIMULATION MODE: Forcing robotik completed state');
				handleNavigateToHistory();
				return;
			}

			// If inquiry ID exists, user has already submitted request before
			// Skip reference step and go directly to result/loading
			if (slikInquiryId && slikInquiryId > 0) {
				// Condition 0: CCA hasn't submitted SLIK MPP yet
				if (
					result.roboticSlikRequestResult?.status === 'REQUESTED' &&
					result.roboticSlikRequestResult.details?.length === 0
				) {
					setRequestStep('loading'); // Go to loading to check status
					return;
				}

				// Condition 1: Manual SLIK
				if (result.isManualSlik) {
					setShowManualModal(true);
					return;
				}

				// Condition 2: SLIK Robotik not enabled
				if (!result.isEnableSlikRobotic) {
					setShowManualModal(true);
					return;
				}

				// Condition 3: SLIK Robotik enabled
				if (result.isEnableSlikRobotic) {
					// Check if SLIK Robotik has completed results
					if (result.roboticSlikResult && result.roboticSlikResult.length > 0) {
						// Navigate to HistorySLIKMPPPage
						handleNavigateToHistory();
						return;
					}

					// Check robotic request status
					const roboticResult =
						result.roboticSlikRequestResult as SlikRobotikResult;
					if (roboticResult) {
						if (
							roboticResult.status === 'FAILED' ||
							roboticResult.status === 'CANCELLED' ||
							roboticResult.status === 'NOTFOUND' ||
							roboticResult.details?.every(
								(detail) => detail.status === 'Failed',
							) ||
							roboticResult.details?.every(
								(detail) => detail.status === 'Cancelled',
							) ||
							roboticResult.details?.every(
								(detail) => detail.status === 'NotFound',
							)
						) {
							// All failed, use manual flow
							setShowManualModal(true);
							return;
						}

						if (roboticResult.status === 'REQUESTED') {
							// Check if all requests are done
							const hasRequested = roboticResult.details?.some(
								(detail) => detail.status === 'Requested',
							);
							if (!hasRequested) {
								// All done, navigate to history
								handleNavigateToHistory();
								return;
							}
							// Still in progress
							setRequestStep('result');
							return;
						}
					}
				}

				// Default to result step
				setRequestStep('result');
			} else {
				// First time opening modal - check if already submitted
				// Condition 0: CCA hasn't submitted SLIK MPP yet
				if (
					result.roboticSlikRequestResult?.status === 'REQUESTED' &&
					result.roboticSlikRequestResult.details?.length === 0
				) {
					setRequestStep('initial');
					return;
				}

				// If there's any existing result, go to loading to check status
				setRequestStep('loading');
			}

			// SIMULATION: Also check for different SLIK states in first-time case
			if (SIMULATION_MODE === 'manual') {
				console.log(
					'🔧 SIMULATION MODE: Forcing manual SLIK state (first-time case)',
				);
				setShowManualModal(true);
				return;
			}

			if (SIMULATION_MODE === 'robotik_completed') {
				console.log(
					'🔧 SIMULATION MODE: Forcing robotik completed state (first-time case)',
				);
				handleNavigateToHistory();
				return;
			}
		}
	}, [
		isCCA,
		slikResultQuery.data,
		data?.mppSlikInquiryId,
		handleNavigateToHistory,
		slikInquiryId,
		SIMULATION_MODE,
	]);

	const handleFRMRequest = async () => {
		try {
			// Check if both files are available
			if (!data!.slikFormFile || !data!.applicationLetterFile) {
				throw new Error('Dokumen FAPP dan PDP wajib diisi');
			}

			// Prepare payload with FAPP and PDP files
			const payload = {
				customerId: data!.customerId,
				slikFormFile: {
					id: data!.slikFormFile.id || '',
					name: data!.slikFormFile.name,
					path: data!.slikFormFile.url, // Use URL as path
				} as FilePayload,
				applicationLetterFile: {
					id: data!.applicationLetterFile.id || '',
					name: data!.applicationLetterFile.name,
					path: data!.applicationLetterFile.url, // Use URL as path
				} as FilePayload,
			};

			await submitSlikRequestFilesMutation.mutateAsync(payload);

			// After successful submission, close the modal
			close({
				slikInquiryReffNumber: '',
				mppSlikInquiryId: 0, // Will be set by backend
			});
		} catch (error) {
			console.error('FRM SLIK request failed:', error);
		}
	};

	const handleCCARequest = async () => {
		console.log('handleCCARequest', data);
		setRequestStep('requesting');
		try {
			const res = await requestMPPSlikMutation.mutateAsync(data!.customerId!);
			setSlikInquiryId(res.data.mppSlikInquiryId);
			setReferenceNumber(res.data.slikInquiryReffNumber || '');
			setRequestStep('reference');
		} catch (error) {
			console.error('CCA SLIK request failed:', error);
			setRequestStep('initial');
		}
	};

	const handleManualSLIK = () => {
		setShowManualModal(true);
	};

	const renderFRMContent = () => (
		<div style={{ marginBottom: 20 }}>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Add Dokumen FAPP & PDP</FleetTitle>
			</div>
			<Form {...ANTD_DEFAULT_PROPS.FORM}>
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="View FAPP">
							{data?.slikFormFile && (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(data.slikFormFile!);
									}}
								>
									{data.slikFormFile.name}
								</a>
							)}
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="View PDP">
							{data?.applicationLetterFile && (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(data.applicationLetterFile!);
									}}
								>
									{data.applicationLetterFile.name}
								</a>
							)}
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</div>
	);

	const renderCCAContent = () => {
		switch (requestStep) {
			case 'reference':
				return (
					<Space
						direction="vertical"
						style={{
							display: 'flex',
							alignItems: 'center',
							textAlign: 'center',
						}}
						size="large"
					>
						<FleetTitle>Reference Number Berhasil Dibuat</FleetTitle>
						<Alert
							message="Refference Number Anda berhasil terbentuk!"
							description={
								<div style={{ textAlign: 'center' }}>
									Berikut adalah Refference Number Anda:
									<br />
									<strong style={{ fontSize: '18px', color: '#1890ff' }}>
										{referenceNumber}
									</strong>
								</div>
							}
							type="success"
							showIcon
							style={{ textAlign: 'center' }}
						/>
						<Typography.Text>
							Klik "Next" untuk melanjutkan ke proses pengecekan SLIK.
						</Typography.Text>
					</Space>
				);

			case 'loading':
				return (
					<Space
						direction="vertical"
						style={{ display: 'flex', alignItems: 'center' }}
						size="large"
					>
						<FleetTitle>Loading SLIK Status...</FleetTitle>
						<Typography.Text>
							Checking SLIK processing status. Please wait...
						</Typography.Text>
					</Space>
				);

			case 'initial':
				return (
					<Space
						direction="vertical"
						style={{ display: 'flex' }}
						size="large"
					>
						<FleetTitle>Request SLIK Result</FleetTitle>
						{/* <Alert
							message="SLIK Request for CCA"
							description={`Click REQUEST to initiate SLIK checking process. ${requestStep}`}
							type="info"
							showIcon
						/> */}
						{data?.slikFormFile && data?.applicationLetterFile && (
							<div>
								<Row gutter={40}>
									<Col span={12}>
										<Form.Item label="View FAPP">
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													openPreviewModal(data.slikFormFile!);
												}}
											>
												{data.slikFormFile.name}
											</a>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item label="View PDP">
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													openPreviewModal(data.applicationLetterFile!);
												}}
											>
												{data.applicationLetterFile.name}
											</a>
										</Form.Item>
									</Col>
								</Row>
							</div>
						)}
					</Space>
				);

			case 'requesting':
				return (
					<Space
						direction="vertical"
						style={{ display: 'flex', alignItems: 'center' }}
						size="large"
					>
						<FleetTitle>Requesting SLIK...</FleetTitle>
						<Typography.Text>
							Processing your SLIK request. Please wait...
						</Typography.Text>
						{/* Simple Progress UI following MppListPage pattern */}
						<div style={{ width: '100%', padding: '0 20px' }}>
							<Flex
								justify="end"
								style={{ marginBottom: -16 }}
							>
								<span>0</span>/<span>6</span>
							</Flex>
							<Progress
								percent={Math.round((0 / 6) * 100)}
								percentPosition={{ align: 'center', type: 'outer' }}
							/>
						</div>
					</Space>
				);

			case 'result':
				// Only show result when we have a valid inquiry ID
				if (!slikInquiryId || slikInquiryId <= 0) {
					return (
						<Space
							direction="vertical"
							style={{ display: 'flex', alignItems: 'center' }}
							size="large"
						>
							<FleetTitle>SLIK Result</FleetTitle>
							<Alert
								message="Invalid Request"
								description="No valid SLIK inquiry ID found. Please try requesting again."
								type="error"
								showIcon
							/>
						</Space>
					);
				}

				if (slikResultQuery.data) {
					const [result] = slikResultQuery.data;
					const isFailedRobotik =
						result.roboticSlikRequestResult?.status === 'FAILED' ||
						result.roboticSlikRequestResult?.details?.every(
							(detail) => detail.status === 'Failed',
						);
					const slikRobotikResults =
						result.roboticSlikRequestResult?.details ?? [];
					const slikInProgress = slikRobotikResults.filter(
						(item) => item.status !== 'Requested',
					).length;
					const slikTotal = slikRobotikResults.length;

					return (
						<Space
							direction="vertical"
							style={{ display: 'flex' }}
							size="large"
						>
							<FleetTitle>SLIK Result</FleetTitle>

							{isFailedRobotik ? (
								<Alert
									message="SLIK Robotik Gagal"
									description="Terdapat kendala pada sistem robotik. Silakan gunakan input manual."
									type="warning"
									showIcon
								/>
							) : (
								<div style={{ width: '100%', padding: '16px 0' }}>
									<div
										style={{
											textAlign: 'center',
											margin: '0 auto 16px',
											maxWidth: 320,
										}}
									>
										<span
											style={{
												fontWeight: 600,
												fontSize: 16,
												textAlign: 'center',
											}}
										>
											Proses pengecekan SLIK sedang berlangsung, harap tunggu
											beberapa saat....
										</span>
									</div>

									<div style={{ maxWidth: 320, margin: '0 auto' }}>
										<Flex justify="center">
											<span>{slikInProgress}</span>/<span>{slikTotal}</span>
										</Flex>
										<Progress
											percent={Math.round((slikInProgress / slikTotal) * 100)}
											showInfo={false}
										/>
									</div>
								</div>
							)}
						</Space>
					);
				}

				if (slikResultQuery.error) {
					return (
						<Space
							direction="vertical"
							style={{ display: 'flex' }}
							size="large"
						>
							<FleetTitle>SLIK Result</FleetTitle>
							<Alert
								message="Error Loading SLIK Result"
								description="Terjadi kendala pada sistem. Silakan coba lagi."
								type="error"
								showIcon
							/>
						</Space>
					);
				}

				return (
					<Space
						direction="vertical"
						style={{ display: 'flex', alignItems: 'center' }}
						size="large"
					>
						<FleetTitle>SLIK Result</FleetTitle>
						<Typography.Text>
							Sedang mengecek hasil SLIK. Silakan tunggu beberapa saat...
						</Typography.Text>
					</Space>
				);

			default:
				return null;
		}
	};

	const renderFooter = () => {
		if (isFRM) {
			return {
				cancelText: 'RETURN',
				okText: 'REQUEST',
				okButtonProps: {
					disabled: submitSlikRequestFilesMutation.isPending,
					loading: submitSlikRequestFilesMutation.isPending,
				},
				onCancel: () => close(),
				onOk: handleFRMRequest,
			};
		}

		if (isCCA) {
			switch (requestStep) {
				case 'reference':
					return {
						cancelText: 'Back',
						okText: 'Next',
						onCancel: () => close(),
						onOk: () => setRequestStep('loading'),
					};

				case 'loading':
					return {
						cancelText: 'Back',
						okButtonProps: { disabled: true },
						okText: 'LOADING...',
						onCancel: () => close(),
					};

				case 'initial':
					return {
						cancelText: 'Back',
						okText: 'REQUEST SLIK',
						okButtonProps: {
							loading: requestMPPSlikMutation.isPending,
						},
						onCancel: () => close(),
						onOk: handleCCARequest,
					};

				case 'result':
					// Only show action buttons when we have a valid inquiry ID
					if (!slikInquiryId || slikInquiryId <= 0) {
						return {
							cancelText: 'Back',
							okText: 'RETRY',
							onCancel: () => close(),
							onOk: () => setRequestStep('initial'),
						};
					}

					if (slikResultQuery.data) {
						const [result] = slikResultQuery.data;
						const isFailedRobotik =
							result.roboticSlikRequestResult?.status === 'FAILED' ||
							result.roboticSlikRequestResult?.details?.every(
								(detail) => detail.status === 'Failed',
							);
						const slikRobotikResults =
							result.roboticSlikRequestResult?.details ?? [];

						const slikInProgress = slikRobotikResults.filter(
							(item) => item.status !== 'Requested',
						).length;
						const slikTotal = slikRobotikResults.length;

						if (isFailedRobotik) {
							return {
								cancelText: 'Back',
								okText: 'INPUT MANUAL SLIK',
								onCancel: () => close(),
								onOk: handleManualSLIK,
							};
						} else if (slikInProgress === slikTotal && slikTotal > 0) {
							// All done
							return {
								cancelText: 'Back',
								okText: 'VIEW RESULT',
								onCancel: () => close(),
								onOk: handleNavigateToHistory,
							};
						} else {
							// Still in progress
							return {
								cancelText: 'Back',
								okButtonProps: { style: { display: 'none' } },
								onCancel: () => close(),
							};
						}
					}

					if (slikResultQuery.error) {
						return {
							cancelText: 'Back',
							okText: 'RETRY',
							onCancel: () => close(),
							onOk: () => setRequestStep('initial'),
						};
					}

					// Still loading or waiting for result
					return {
						cancelText: 'Back',
						okButtonProps: { disabled: true },
						okText: 'WAITING...',
						onCancel: () => close(),
					};

				default:
					return {
						cancelText: 'Back',
						onCancel: () => close(),
					};
			}
		}

		return {};
	};

	return (
		<>
			<Modal
				{...modal}
				width={isFRM ? 940 : 1000}
				{...renderFooter()}
			>
				{isFRM ? renderFRMContent() : renderCCAContent()}
			</Modal>

			{showManualModal && (
				<ManualSLIKInputModal
					modal={{
						open: true,
						onCancel: () => setShowManualModal(false),
					}}
					close={() => {
						setShowManualModal(false);
						close();
					}}
					data={{
						mppSlikInquiryId: slikInquiryId || 0,
						customerId: data?.customerId || '',
					}}
				/>
			)}
		</>
	);
};

export default RequestSLIKModal;
